/**
 * What the discard confirmation says while a recording is live (guideline
 * v1.3 as adopted by John, 2026-10-22; claro-design README "Recording screen").
 *
 * The dialog itself stays the app's own confirm (`ConfirmDialog`); v1.3's
 * custom card is not used. Its content is: the title "Discard this
 * recording?", a body naming the real length and patient when known, the
 * destructive Discard, and "Keep recording" as the safe default.
 *
 * The body says only what is true on the web:
 * - `withNote`: the draft note goes with the recording. Cancel recording does
 *   this when the recording created its own draft and nothing was added to
 *   it (`discardTakesDraft`, CLA-1875 / CLA-1880), and so does discarding the
 *   note while it records.
 * - otherwise the note is kept as it is: one that already existed, or a draft
 *   the clinician typed into. The server takes the recording off it
 *   (`abandon_capture`, CLA-1880). It still does not say the recording "will
 *   be deleted": against a server without that endpoint the streamed audio
 *   stays on the note's server copy. Either way it won't be added.
 * Neither says "This can't be undone": a discarded note can be restored
 * server-side (`POST /notes/:id/restore`).
 */
export interface DiscardRecordingCopy {
  title: string
  body: string
}

export function discardRecordingCopy({
  length,
  patientName,
  withNote,
}: {
  /** The recording's length as the clock shows it (m:ss), or null if unknown. */
  length: string | null
  patientName?: string | null
  /** True when the draft note is discarded too; false when the note is kept. */
  withNote: boolean
}): DiscardRecordingCopy {
  const name = patientName?.trim()
  const subject = `${length ? `The ${length} recording` : 'This recording'}${name ? ` with ${name}` : ''}`
  return {
    title: 'Discard this recording?',
    body: withNote
      ? `${subject} and its draft note will be deleted.`
      : `${subject} won't be added to this note. The note stays as it is.`,
  }
}
